import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import toastr from 'COMPONENTS/ShowTostr/ShowTostr';
import { getMessage } from "CONFIG/i18n";

// Selector
import {
  getOperatorStatus,
  getSessionName,
  getSessionKey,
  getSessionCreatedTime,
  getIsTransfer,
  getNumberOfCallsWaiting,
  getTransferDestinationOperatorList,
  getIsTransferError,
  getRequirement,
  getIsWebWorkerError,
} from 'REDUX/Selector/SupportCenterSelector';
import { selectUserName } from "COMPONENTS/MainLayout/selectors";
import { getCustomerId, getUserId } from "REDUX/Selector/UserSelector";

// webSocket
import { socketMessage } from "WEBSOCKET/constants";
import {
  sendWebsocketMessage
} from "WEBSOCKET/WebsocketHandler";
import {
  sendWebsocketMessage as sendSupportCenterWebsocketMessage,
} from "WEBSOCKET/SupportCenterWebsocketHandler";

// Utilities
import { CONFIG, OPERATOR_STATUS } from "UTILS/Constants";

// Action
import { sessionActions, callWaitingAction } from "CONFIG/ActionFactory";

// Components
import withErrorHandler from "COMPONENTS/withErrorHandler/withErrorHandler";
import CallWaiting from "./CallWaiting";
import TransferDestinationModal from './TransferDestinationModal';

// Other
import sound from "ASSETS/user-visit.mp3";
import Push from "push.js";

const CallWaitingContainer = (props) => {
  const operatorStatus = useSelector(getOperatorStatus);
  const sessionName = useSelector(getSessionName);
  const sessionKey = useSelector(getSessionKey);
  const sessionCreatedTime = useSelector(getSessionCreatedTime);
  const isTransfer = useSelector(getIsTransfer);
  const numberOfCallsWaiting = useSelector(getNumberOfCallsWaiting);
  const userName = useSelector(selectUserName);
  const transferDestinationOperatorList = useSelector(getTransferDestinationOperatorList);
  const isTransferError = useSelector(getIsTransferError);
  const requirement = useSelector(getRequirement);
  const customerId = useSelector(getCustomerId);
  const userId = useSelector(getUserId);
  const isWebWorkerError = useSelector(getIsWebWorkerError);

  const audioElement = new Audio(sound);
  // To reference the latest operatorStatus within the callback function of setTimeout.
  const operatorStatusRef = useRef(operatorStatus);
  const nextOperatorStatusRef = useRef(null);

  const [
    showTransferDestinationModalFlag,
    setShowTransferDestinationModalFlag,
  ] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [isOffline, setIsOffline] = useState(true);

  useEffect(() => {
    let timeoutId;
    if (operatorStatus === OPERATOR_STATUS.CORRESPONDENCE) {
      changeStatus(OPERATOR_STATUS.CORRESPONDENCE);
      timeoutId = setTimeout(() => {
        if (operatorStatusRef.current === OPERATOR_STATUS.CORRESPONDENCE) {
          changeStatus(OPERATOR_STATUS.WAITING);
        }
      }, 20 * 1000);
    } else {
      changeStatus(OPERATOR_STATUS.WAITING);
    }

    // online/offline detection
    window.addEventListener('offline', () => { setIsOffline(true) });
    window.addEventListener('online', () => { setIsOffline(false) });

    switch (Push.Permission.get()) {
      case Push.Permission.DEFAULT:
        Push.Permission.request();
        break;
      case Push.Permission.DENIED:
        toastr.info(getMessage("QR_WAITING_PUSH_DENIED"));
        break;
      default:
        break;
    }
    // Prevent you from going back using the browser's back button.
    history.pushState(null, null, location.href);
    window.onpopstate = function (event) {
      history.go(1);
    };

    if (timeoutId) {
      return () => {clearTimeout(timeoutId)};
    }
  }, []);

  useEffect(() => {
    const prevOperatorStatus = operatorStatusRef.current;
    operatorStatusRef.current = operatorStatus;
    if (operatorStatus === OPERATOR_STATUS.CALLING) {
      if (document.hasFocus()) {
        audioElement.play();
      }
      if (Push.Permission.has()) {
        Push.create(getMessage("QR_WAITING_PUSH_TITLE"), {
          body: getMessage("QR_WAITING_PUSH_MSG"),
          timeout: 300000,
          onClick: function () {
            window.focus();
            this.close();
          }
        });
      }
      let blinkFlag = true;
      function setTitleMsgFlash() {
        document.getElementsByTagName("title")[0].innerHTML = blinkFlag ? 'Finplex eXcloser'
          : getMessage("QR_WAITING_BROWSER_BLINK");
        blinkFlag = !blinkFlag;
        if (document.hasFocus() && !blinkFlag) {
          if (Push.Permission.has()) {
            Push.clear();
          }
        } else {
          setTimeout(setTitleMsgFlash, 1000);
        }
      }
      setTitleMsgFlash();
    }

    if (prevOperatorStatus === OPERATOR_STATUS.CALLING &&
      operatorStatus === OPERATOR_STATUS.WAITING &&
      nextOperatorStatusRef.current !== OPERATOR_STATUS.WAITING) {
      // If the transfer is successful, simply hide the transfer destination modal.
      // If guest becomes absent while the transfer destination modal is displayed,
      // a message is displayed and the transfer destination modal is hidden.
      if (isTransferring) {
        setIsTransferring(false);
      } else {
        toastr.info(getMessage("GUEST_CANCELED_SESSION"));
      }
      cancelTransfer();
    }
    if (operatorStatus === nextOperatorStatusRef.current) {
      nextOperatorStatusRef.current = null;
    }
  }, [operatorStatus]);

  useEffect(() => {
    if (isTransferring) {
      setIsTransferring(false);
    }
  }, [isTransferError]);

  // After a while after the operator's network is disconnected, 
  // the operator status recorded in the DB changes to "ABSENCE".
  // Therefore, it is necessary to send the status to the server 
  // after recovering from a network disconnection.
  useEffect(() => {
    if (!isOffline) {
      if (operatorStatus === OPERATOR_STATUS.LEAVING) {
        changeStatus(OPERATOR_STATUS.LEAVING);
      } else {
        changeStatus(OPERATOR_STATUS.WAITING);
      }
    }
  }, [isOffline]);

  useEffect(() => {
    if (isWebWorkerError) {
      toastr.error(getMessage("SERVER_UNREACHABLE_ERROR"));
    }
  }, [isWebWorkerError]);

  useEffect(() => {
    return () => {
      callWaitingAction.setIsWebWorkerError(false);
    };
  }, []);

  const backToMenu = () => {
    props.history.push(CONFIG.path.createSession);
  };

  const startSession = () => {
    sessionActions.joinSessionAction(sessionKey).then(
      response => {
        const sessionJoinPayload = {
          data: {
            category: socketMessage.category.SESSION,
            action: socketMessage.subCategories.SESSION.JOIN,
            userName,
            sessionKey: response.data.data.sessionKey,
            token: response.data.data.token,
            isOperator: true,
          }
        };
        props.history.push(CONFIG.path.dashboard);
        sendWebsocketMessage(
          socketMessage.events.MESSAGE,
          sessionJoinPayload
        )();
        callWaitingAction.setOperatorStatus(OPERATOR_STATUS.CORRESPONDENCE);
      },
      error => {
        toastr.error(getMessage(error.code));
      }
    );
  };

  const changeStatus = (newStatus) => {
    nextOperatorStatusRef.current = newStatus;
    sendSupportCenterWebsocketMessage(
      socketMessage.events.MESSAGE,
      {data: {
        category: socketMessage.category.SUPPORT_CENTER,
        action: socketMessage.subCategories.SUPPORT_CENTER.CHANGE_STATUS,
        status: newStatus,
        isOperator: true,
        customerId,
        userId,
      }}
    )();
  };

  const showTransferDestinationModal = () => {
    setShowTransferDestinationModalFlag(true);
    sendSupportCenterWebsocketMessage(
      socketMessage.events.MESSAGE,
      {data: {
        category: socketMessage.category.SUPPORT_CENTER,
        action: socketMessage.subCategories.SUPPORT_CENTER.GET_CALL_TRANSFER_INFO,
        requirement,
        isOperator: true,
        customerId,
        userId,
      }}
    )();
  };

  const transferSession = (operator) => {
    setIsTransferring(true);
    sendSupportCenterWebsocketMessage(
      socketMessage.events.MESSAGE,
      {data: {
        category: socketMessage.category.SUPPORT_CENTER,
        action: socketMessage.subCategories.SUPPORT_CENTER.CALL_TRANSFER,
        operator,
        isOperator: true,
        customerId,
        userId,
      }}
    )();
    // Don't hide transfer destination modal yet.
    // Hide the modal when a message (status=WAITING) is returned from the server.
  };

  const cancelTransfer = () => {
    setShowTransferDestinationModalFlag(false);
    callWaitingAction.setTransferDestinationOperatorList([]);
  };

  return (
    <>
      <CallWaiting
        backToMenu={backToMenu}
        operatorStatus={operatorStatus}
        sessionName={sessionName}
        sessionCreatedTime={sessionCreatedTime}
        isTransfer={isTransfer}
        numberOfCallsWaiting={numberOfCallsWaiting}
        startSession={startSession}
        changeStatus={changeStatus}
        showTransferDestinationModal={showTransferDestinationModal}
        isWebWorkerError={isWebWorkerError}
      />
      <TransferDestinationModal
        show={showTransferDestinationModalFlag}
        operatorList={transferDestinationOperatorList}
        handleCancelClick={cancelTransfer}
        handleTransferClick={transferSession}
        isTransferError={isTransferError}
        setIsTransferError={(isTransferError) => {
          callWaitingAction.setIsTransferError(isTransferError);
        }}
      />
    </>
  );

};

export default withErrorHandler(CallWaitingContainer);
